import { useEffect, useState } from "react";

export interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function unionRect(nodes: ArrayLike<Element>): TargetRect | null {
  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!node) {
      continue;
    }
    const box = node.getBoundingClientRect();
    top = Math.min(top, box.top);
    left = Math.min(left, box.left);
    right = Math.max(right, box.right);
    bottom = Math.max(bottom, box.bottom);
  }
  if (top === Infinity) {
    return null;
  }
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * Tracks the viewport bounding box of the element(s) marked `data-tutorial=id`.
 * When several elements share the id (e.g. every suspicious log row) the
 * spotlight covers their union. Returns null while no element is present (the
 * overlay treats that as a missing target and falls back). Re-measures on
 * resize, scroll and when the step changes.
 */
export function useTargetRect(targetId: string | null): TargetRect | null {
  const [rect, setRect] = useState<TargetRect | null>(null);

  useEffect(() => {
    if (!targetId) {
      setRect(null);
      return;
    }

    let frame = 0;
    const measure = (): void => {
      const nodes = document.querySelectorAll(`[data-tutorial="${targetId}"]`);
      setRect(unionRect(nodes));
    };

    const scheduleMeasure = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    scheduleMeasure();
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);
    const interval = window.setInterval(measure, 400);

    return () => {
      cancelAnimationFrame(frame);
      window.clearInterval(interval);
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
    };
  }, [targetId]);

  return rect;
}
